import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Divider,
  Box,
  MenuItem,
  DialogContentText
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import api from '../../utils/api';

const CustomerDialog = ({ open, handleClose, onSave, customer: initialCustomer = null }) => {
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);
  const [openNewTypeDialog, setOpenNewTypeDialog] = useState(false);
  const [newType, setNewType] = useState('');
  const [vehicleTypes, setVehicleTypes] = useState(['Bici Elettrica', 'Bici Muscolare', 'Monopattino', 'Altro']);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    countryCode: '39',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    notes: '',
    vehicles: [{
      brand: '',
      model: '',
      type: 'Bici Elettrica',
      serialNumber: '',
      color: '',
      notes: ''
    }]
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Check if this is a vehicle field (contains vehicle index)
    if (name.includes('vehicle-')) {
      const parts = name.split('-');
      const vehicleIndex = parseInt(parts[1]);
      const fieldName = parts[2];
      
      // Gestione speciale per l'aggiunta di una nuova tipologia
      if (fieldName === 'type' && value === 'new_type') {
        setOpenNewTypeDialog(true);
        return;
      }
      
      const updatedVehicles = [...formData.vehicles];
      updatedVehicles[vehicleIndex] = {
        ...updatedVehicles[vehicleIndex],
        [fieldName]: fieldName === 'type' ? value : value.toUpperCase()
      };
      
      setFormData({
        ...formData,
        vehicles: updatedVehicles
      });
    } else {
      // Regular field - convert to uppercase
      setFormData({
        ...formData,
        [name]: value.toUpperCase()
      });
    }
  };
  
  const addVehicle = () => {
    setFormData({
      ...formData,
      vehicles: [
        ...formData.vehicles,
        {
          brand: '',
          model: '',
          type: 'Bici Elettrica',
          serialNumber: '',
          color: '',
          notes: ''
        }
      ]
    });
  };
  
  const removeVehicle = (index) => {
    if (formData.vehicles.length > 1) {
      const updatedVehicles = formData.vehicles.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        vehicles: updatedVehicles
      });
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = ['firstName', 'lastName', 'phone'];
    const emptyFields = requiredFields.filter(field => !formData[field].trim());
    
    if (emptyFields.length > 0) {
      const fieldNames = {
        firstName: 'Nome',
        lastName: 'Cognome',
        phone: 'Telefono'
      };
      
      const missingFields = emptyFields.map(field => fieldNames[field]).join(', ');
      alert(`I seguenti campi sono obbligatori: ${missingFields}`);
      return;
    }
    
    // Validate email format if provided
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      alert('Formato email non valido');
      return;
    }
    
    // Validate vehicle required fields
    const vehicleRequiredFields = ['brand', 'model', 'type'];
    let invalidVehicles = false;
    
    formData.vehicles.forEach((vehicle, index) => {
      const emptyVehicleFields = vehicleRequiredFields.filter(field => !vehicle[field]);
      if (emptyVehicleFields.length > 0) {
        const fieldNames = {
          brand: 'Marca',
          model: 'Modello',
          type: 'Tipologia'
        };
        
        const missingFields = emptyVehicleFields.map(field => fieldNames[field]).join(', ');
        alert(`Veicolo ${index + 1}: I seguenti campi sono obbligatori: ${missingFields}`);
        invalidVehicles = true;
      }
    });
    
    if (invalidVehicles) return;
    
    if (loading || submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      let customerResponse;
      const customerId = initialCustomer ? initialCustomer.id : null;
      
      if (initialCustomer) {
        // Update existing customer
        customerResponse = await api.put(`/customers/${customerId}`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          notes: formData.notes
        });
        
        // Get existing vehicles
        const existingVehiclesRes = await api.get(`/vehicles/customer/${customerId}`);
        const existingVehicles = existingVehiclesRes.data || [];
        
        const norm = (s) => String(s || '').trim().toLowerCase();
        const sameVehicle = (a, b) => norm(a.brand) === norm(b.brand) && norm(a.model) === norm(b.model) && norm(a.serialNumber) === norm(b.serialNumber);
        
        const createdKeys = new Set();
        for (const vehicle of formData.vehicles) {
          if (!vehicle.brand || !vehicle.model) continue;
          if (vehicle.id) {
            await api.put(`/vehicles/${vehicle.id}`, { ...vehicle, customerId });
          } else {
            const existingVehicle = existingVehicles.find(v => sameVehicle(v, vehicle));
            if (existingVehicle) {
              await api.put(`/vehicles/${existingVehicle.id}`, { ...vehicle, customerId });
            } else {
              const key = `${norm(vehicle.brand)}|${norm(vehicle.model)}|${norm(vehicle.serialNumber)}`;
              if (createdKeys.has(key)) continue;
              createdKeys.add(key);
              await api.post('/vehicles', { ...vehicle, customerId });
            }
          }
        }
      } else {
        // Create new customer
        customerResponse = await api.post('/customers', {
          firstName: formData.firstName,
          lastName: formData.lastName,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          notes: formData.notes
        });
        
        const newCustomerId = customerResponse.data.id;
        
        // Create vehicles (only once per brand+model+serialNumber to avoid duplicates)
        const createdKeys = new Set();
        for (const vehicle of formData.vehicles) {
          if (vehicle.brand && vehicle.model) {
            const key = `${vehicle.brand}|${vehicle.model}|${vehicle.serialNumber || ''}`;
            if (createdKeys.has(key)) continue;
            createdKeys.add(key);
            await api.post('/vehicles', {
              ...vehicle,
              customerId: newCustomerId
            });
          }
        }
      }
      
      // Call onSave with the customer data
      onSave(customerResponse.data);
      handleClose();
    } catch (err) {
      console.error('Error saving customer or vehicles:', err);
      alert(`Errore nel salvare i dati: ${err.message}`);
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (submittingRef.current || loading) return;
    handleSubmit();
  };

  const handleAddNewType = () => {
    if (newType.trim()) {
      setVehicleTypes([...vehicleTypes, newType.trim()]);
      setOpenNewTypeDialog(false);
      setNewType('');
    }
  };

  const handleNewTypeChange = (e) => {
    setNewType(e.target.value);
  };

  // Popola il form quando viene passato un customer esistente
  useEffect(() => {
    if (open && initialCustomer) {
      // Carica i veicoli del cliente
      const loadCustomerData = async () => {
        try {
          const vehiclesRes = await api.get(`/vehicles/customer/${initialCustomer.id}`);
          const vehicles = vehiclesRes.data.length > 0 ? vehiclesRes.data.map(v => ({
            id: v.id,
            brand: v.brand || '',
            model: v.model || '',
            type: v.type || 'Bici Elettrica',
            serialNumber: v.serialNumber || '',
            color: v.color || '',
            notes: v.notes || ''
          })) : [{
            brand: '',
            model: '',
            type: 'Bici Elettrica',
            serialNumber: '',
            color: '',
            notes: ''
          }];

          setFormData({
            firstName: initialCustomer.firstName || '',
            lastName: initialCustomer.lastName || '',
            countryCode: initialCustomer.countryCode || '39',
            phone: initialCustomer.phone || '',
            email: initialCustomer.email || '',
            address: initialCustomer.address || '',
            city: initialCustomer.city || '',
            postalCode: initialCustomer.postalCode || '',
            notes: initialCustomer.notes || '',
            vehicles: vehicles
          });
        } catch (err) {
          console.error('Error loading customer data:', err);
        }
      };
      loadCustomerData();
    } else if (open && !initialCustomer) {
      // Reset form per nuovo cliente
      setFormData({
        firstName: '',
        lastName: '',
        countryCode: '39',
        phone: '',
        email: '',
        address: '',
        city: '',
        postalCode: '',
        notes: '',
        vehicles: [{
          brand: '',
          model: '',
          type: 'Bici Elettrica',
          serialNumber: '',
          color: '',
          notes: ''
        }]
      });
    }
  }, [open, initialCustomer]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialCustomer ? 'Modifica Cliente' : 'Nuovo Cliente'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="lastName"
              label="Cognome *"
              fullWidth
              value={formData.lastName}
              onChange={handleInputChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="firstName"
              label="Nome *"
              fullWidth
              value={formData.firstName}
              onChange={handleInputChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <TextField
                  name="countryCode"
                  label="Prefisso"
                  fullWidth
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  required
                  placeholder="39"
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>
              <Grid item xs={9}>
                <TextField
                  name="phone"
                  label="Telefono *"
                  fullWidth
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="email"
              label="Email"
              fullWidth
              value={formData.email}
              onChange={handleInputChange}
              type="email"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="address"
              label="Indirizzo"
              fullWidth
              value={formData.address}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="city"
              label="Città"
              fullWidth
              value={formData.city}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="postalCode"
              label="CAP"
              fullWidth
              value={formData.postalCode}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="notes"
              label="Note"
              fullWidth
              multiline
              rows={2}
              value={formData.notes}
              onChange={handleInputChange}
            />
          </Grid>

          {/* Vehicles section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Veicoli
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {formData.vehicles.map((vehicle, index) => (
              <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      Veicolo {index + 1}
                      {formData.vehicles.length > 1 && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeVehicle(index)}
                          sx={{ ml: 2 }}
                        >
                          Rimuovi
                        </Button>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name={`vehicle-${index}-brand`}
                      label="Marca *"
                      fullWidth
                      value={vehicle.brand}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name={`vehicle-${index}-model`}
                      label="Modello *"
                      fullWidth
                      value={vehicle.model}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name={`vehicle-${index}-type`}
                      label="Tipologia *"
                      fullWidth
                      select
                      value={vehicle.type}
                      onChange={handleInputChange}
                      required
                    >
                      {vehicleTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                      <MenuItem value="new_type" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                        + Aggiungi nuova tipologia
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name={`vehicle-${index}-serialNumber`}
                      label="Numero Telaio"
                      fullWidth
                      value={vehicle.serialNumber}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name={`vehicle-${index}-color`}
                      label="Colore"
                      fullWidth
                      value={vehicle.color}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name={`vehicle-${index}-notes`}
                      label="Note Veicolo"
                      fullWidth
                      multiline
                      rows={2}
                      value={vehicle.notes}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addVehicle}
              sx={{ mt: 1 }}
            >
              Aggiungi Veicolo
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Annulla</Button>
        <Button 
          type="button"
          onClick={handleSaveClick} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          Salva
        </Button>
      </DialogActions>

      {/* Dialog for adding new type */}
      <Dialog open={openNewTypeDialog} onClose={() => setOpenNewTypeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Aggiungi Nuova Tipologia</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Inserisci il nome della nuova tipologia di veicolo:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            name="newType"
            label="Nome Tipologia"
            type="text"
            fullWidth
            variant="outlined"
            value={newType}
            onChange={handleNewTypeChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewTypeDialog(false)} color="primary">
            Annulla
          </Button>
          <Button onClick={handleAddNewType} color="primary" variant="contained">
            Aggiungi
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CustomerDialog; 